import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useFavorites } from "../lib/store";
import { Loading, PageHero } from "../components/Common";
import { Logo } from "../components/Logo";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";

export function Unavailable({ kind = "property" }) {
  const { lang } = useI18n();
  const b = (en, ar) => (lang === "ar" ? ar : en);
  const back = { property: ["/properties", b("Browse properties", "تصفح العقارات")], project: ["/developments", b("View our developments", "مشاريعنا")], article: ["/news", b("Latest news", "آخر الأخبار")] }[kind];
  return (
    <div data-testid="unavailable-state" className="relative flex min-h-[80vh] items-center overflow-hidden">
      <div className="pattern-mashrabiya absolute inset-0 opacity-30" aria-hidden="true" />
      <div className="container-lux relative py-32 text-center">
        <p className="eyebrow mb-4">{b("Not found", "غير موجود")}</p>
        <h1 className="h-display mx-auto max-w-2xl text-4xl sm:text-5xl">{b(`This ${kind === "article" ? "article" : kind === "project" ? "development" : "listing"} is no longer available`, kind === "article" ? "هذا المقال لم يعد متاحاً" : kind === "project" ? "هذا المشروع لم يعد متاحاً" : "هذا العقار لم يعد متاحاً")}</h1>
        <p className="mx-auto mt-5 max-w-lg text-fg/60">{b("It may have been sold, rented or unpublished. Explore similar opportunities or talk to our team.", "ربما تم بيعه أو تأجيره أو إلغاء نشره. استكشف فرصاً مشابهة أو تحدث مع فريقنا.")}</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to={back[0]} className="btn-cta" data-testid="unavailable-browse">{back[1]}</Link>
          <Link to="/contact" className="btn-ghost" data-testid="unavailable-contact">{b("Contact us", "تواصل معنا")}</Link>
        </div>
      </div>
    </div>
  );
}

export function Favorites() {
  const { t } = useI18n();
  const { favs } = useFavorites();
  const { data, loading } = useApi(favs.length ? `/properties/by-ids?ids=${favs.join(",")}` : null);
  return (
    <div data-testid="favorites-page">
      <Seo title={t("fav.title")} />
      <PageHero eyebrow={t("nav.favorites")} title={t("fav.title")} sub={t("fav.sub")} />
      <div className="container-lux py-12">
        {!favs.length ? (
          <div data-testid="favorites-empty" className="glass rounded-sm p-12 text-center">
            <p className="text-fg/60">{t("fav.empty")}</p>
            <Link to="/properties" data-testid="favorites-browse-link" className="btn-cta mt-6">{t("fav.browse")}</Link>
          </div>
        ) : loading ? <Loading /> : (
          <div data-testid="favorites-list" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {favs.map((id) => data?.find((p) => p.id === id)).filter(Boolean).map((p) => (<PropertyCard key={p.id} p={p} />))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminLogin() {
  const { t } = useI18n();
  return (
    <div data-testid="admin-login-page" className="relative flex min-h-[80vh] items-center justify-center px-5 pt-24">
      <Seo title={t("admin.title")} />
      <div className="mashrabiya absolute inset-0 opacity-[0.04]" aria-hidden="true" />
      <div className="glass relative max-w-lg rounded-sm p-10 text-center">
        <Logo />
        <Lock className="mx-auto mt-8 h-8 w-8 text-cta" />
        <h1 data-testid="admin-placeholder-title" className="h-display mt-5 text-3xl">{t("admin.title")}</h1>
        <p className="mt-4 text-fg/60">{t("admin.body")}</p>
        <Link to="/" data-testid="admin-back-home" className="btn-cta mt-8">{t("admin.back")}</Link>
      </div>
    </div>
  );
}

export default function NotFound() {
  const { t } = useI18n();
  return (
    <div data-testid="not-found-page" className="relative flex min-h-[80vh] items-center pt-24">
      <Seo title={t("notFound.title")} />
      <div className="mashrabiya absolute inset-0 opacity-[0.04]" aria-hidden="true" />
      <div className="container-lux relative">
        <p className="font-display text-[8rem] leading-none text-cta/30 md:text-[12rem]">404</p>
        <h1 className="h-display text-4xl sm:text-5xl">{t("notFound.title")}</h1>
        <p className="mt-4 max-w-md text-fg/60">{t("notFound.body")}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/" data-testid="not-found-home-link" className="btn-cta">{t("notFound.home")}</Link>
          <Link to="/properties" data-testid="not-found-properties-link" className="btn-ghost">{t("hero.explore")}</Link>
        </div>
      </div>
    </div>
  );
}
